import {
  Button,
  HeadingText,
  ParagraphText,
  StackItem,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { FormProvider, useForm } from '@leagueplatform/web-common';
import { captureAppError } from '@leagueplatform/observability';
import { TOAST_STATUS, toastActions } from '@leagueplatform/toast-messages';
import { useIntl } from '@leagueplatform/locales';
import { BasePage } from 'components/base-page.component';
import { ProfilePictureForm } from 'components/forms/profile-picture-form.component';
import { ACCOUNT_SETUP_CONTENT_MAX_WIDTH } from 'common/constants/ui';
import {
  PRODUCT_AREA,
  SCREEN_NAMES,
  EVENT_NAME,
  trackAnalyticsEvent,
  usePageViewAnalytics,
} from '@leagueplatform/analytics';
import { RHFTextInput } from 'components/form-elements/rhf-text-input.component';
import {
  emailValidation,
  phoneNumberValidation,
} from 'common/utils/rhf-form-validation.util';
import { useUpdateAccountSetup } from 'hooks/use-update-account-setup/use-update-account-setup.hook';
import { SENTRY_CONSTANTS } from 'common/constants/constants';
import { useIsBelowTablet } from 'hooks/use-is-below-tablet/use-is-below-tablet.hooks';
import { OnboardingMachineContext } from 'components/onboarding/onboarding.machine';
import { useSelector } from '@xstate/react';

type AccountSetupValues = {
  homePhoneNumber?: string;
  email?: string;
};

export const AccountSetupView = () => {
  const { formatMessage } = useIntl();

  const actor = OnboardingMachineContext.useActorRef();
  const { send } = actor;
  const { homePhoneNumber, email, userFullName } = useSelector(
    actor,
    (machineSnapshot) => machineSnapshot.context,
  );

  const navigateToNextOboardingStep = (values?: AccountSetupValues) =>
    send({ type: 'navigateToNextView', payload: { ...values } });

  const methods = useForm({
    defaultValues: {
      homePhoneNumber,
      email,
    },
  });

  const { isDirty, isSubmitting } = methods.formState;

  const { updateAccountSetup } = useUpdateAccountSetup();
  const isBelowTablet = useIsBelowTablet();

  usePageViewAnalytics({
    product_area: PRODUCT_AREA.ONBOARDING,
    screen_name: SCREEN_NAMES.ACCOUNT_SET_UP,
  });

  const handleFormSubmit = async (values: AccountSetupValues) => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.ONBOARDING,
      screen_name: SCREEN_NAMES.ACCOUNT_SET_UP,
      detail: 'continue',
    });
    if (!isDirty) {
      navigateToNextOboardingStep();
    } else {
      try {
        await updateAccountSetup(values);
        navigateToNextOboardingStep(values);
      } catch (error) {
        let errorMessage;
        if (error instanceof Error) {
          captureAppError(error, {
            ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
            errorName: 'Error saving Account setup form',
            context: {
              Context: {
                description: formatMessage({ id: 'STR_ACCOUNT_SETUP' }),
              },
            },
          });
          if (error.message) {
            errorMessage = JSON.parse(error.message);
          }
        }
        toastActions.add({
          type: TOAST_STATUS.ERROR,
          text:
            errorMessage?.info?.reason ||
            formatMessage({ id: 'STR_SOMETHING_WENT_WRONG_TRY_AGAIN' }),
        });
      }
    }
  };

  return (
    <BasePage
      showNavBar={false}
      css={{
        padding: isBelowTablet ? '$none' : '$twoAndHalf',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...(isBelowTablet && {
          '.wrapper': {
            height: '100%',
          },
        }),
      }}
    >
      <StackItem
        css={{
          ...(isBelowTablet && { height: '100%' }),
          maxWidth: isBelowTablet ? '100%' : ACCOUNT_SETUP_CONTENT_MAX_WIDTH,
          background: '$surfaceCardSecondary',
          boxShadow: '$card',
        }}
      >
        <StackLayout
          spacing="$two"
          css={{
            padding: '$two',
            paddingBlockEnd: '$none',
          }}
        >
          <StackItem horizontalAlignment="center" css={{ alignSelf: 'center' }}>
            <HeadingText size="xl" level="2" as="h1">
              {formatMessage({ id: 'STR_ACCOUNT_SETUP_HEADING' })}
            </HeadingText>
            <ParagraphText
              css={{ textAlign: 'center', marginBlockStart: '$half' }}
            >
              {formatMessage({ id: 'STR_ACCOUNT_SETUP_SUBHEADING' })}
            </ParagraphText>
          </StackItem>
          <StackItem horizontalAlignment="center" css={{ alignSelf: 'center' }}>
            <ProfilePictureForm />
            <HeadingText
              size="lg"
              level="2"
              as="h2"
              css={{ marginBlockStart: '$half', textAlign: 'center' }}
            >
              {`[${userFullName}]`}
            </HeadingText>
          </StackItem>
        </StackLayout>
        <FormProvider
          formMethods={methods}
          onSubmit={handleFormSubmit}
          css={{
            display: 'grid',
          }}
        >
          <StackItem
            css={{
              gap: '$one',
              padding: '$two',
            }}
          >
            <RHFTextInput
              id="homePhoneNumber"
              type="tel"
              placeholder={formatMessage({ id: 'STR_PHONE_PLACEHOLDER' })}
              label={formatMessage({ id: 'STR_PRIMARY_PHONE_NUMBER' })}
              required
              registerOptions={phoneNumberValidation(
                formatMessage({ id: 'STR_PHONE_VALIDATION_ERROR' }),
              )}
            />
            <RHFTextInput
              id="email"
              inputMode="email"
              placeholder={formatMessage({ id: 'STR_EMAIL_PLACEHOLDER' })}
              label={formatMessage({ id: 'STR_EMAIL_ADDRESS' })}
              required
              registerOptions={emailValidation(
                formatMessage({ id: 'STR_EMAIL_VALIDATION_ERROR' }),
              )}
            />
          </StackItem>

          <StackItem
            css={{
              borderColor: '$decorativeBrandPrimaryBrightest',
              borderStyle: 'solid',
              borderBlockStartWidth: 'thin',
              paddingBlockStart: '$one',
              paddingBlockEnd: isBelowTablet ? '$five' : '$one',
              paddingInline: '$oneAndHalf',
            }}
            horizontalAlignment="end"
          >
            <Button
              type="submit"
              loading={isSubmitting}
              width={isBelowTablet ? 'fillContainer' : 'hugContents'}
              css={{ flexGrow: 0 }}
            >
              {formatMessage({ id: 'STR_CONTINUE' })}
            </Button>
          </StackItem>
        </FormProvider>
      </StackItem>
    </BasePage>
  );
};
